console.clear()
  
/**
 * IMPORTS
 */
import * as THREE from 'three'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { ScrollSmoother } from 'gsap/ScrollSmoother'
gsap.registerPlugin(ScrollTrigger, ScrollSmoother)

/**
 * VARIABLES
 */
const home = document.querySelector('.home')
const inspi = document.querySelector('.inspi')
const form = document.querySelector('.form')
const canvasHexagonsWhite = document.querySelector('.canvasHexagonsWhite')
const canvasHexagonsBlack = document.querySelector('.canvasHexagonsBlack')
  
/**
 * GET HEIGHT 
 * */
var height = window.innerHeight
document.documentElement.style.setProperty('--screen-height', height + 'px')
window.addEventListener('resize', function () {
  var height = window.innerHeight
  document.documentElement.style.setProperty('--screen-height', height + 'px')
})

/**
 * BURGER MENU
 */
const menuButton = document.querySelector('.nav__button')
const navLinks = document.querySelector('.nav__links')
const nav = document.querySelector('.nav')

if (menuButton && navLinks && nav) {
  menuButton.addEventListener('click', () => {
    const willBeShown = navLinks.classList.contains('nav__links--show')
    navLinks.classList.toggle('nav__links--show')

    if (willBeShown) {
      setTimeout(() => {
        nav.classList.remove('nav--burgerMenu')
        menuButton.classList.remove('nav__button--close') 
        menuButton.textContent = 'Menu'
      }, 300)
    } else {
      nav.classList.add('nav--burgerMenu')
      menuButton.classList.add('nav__button--close') 
      menuButton.textContent = 'Close' 
    }
  })
}

/**
 * HEXAGONS
 */
function initHexCanvas(canvasId, backgroundColor, shadowColor, assignGreen = false) {
  const canvas = document.getElementById(canvasId)
  if (!canvas) return

  const ctx = canvas.getContext('2d')

  let size = 55
  let dx = size * 3 / 2
  let dy = Math.sqrt(3) * size

  let islands = []
  const elevationMap = new Map()
  const phaseMap = new Map()
  let hexPositions = []
  let greenHexIndices = []

  function generateIslands() {
    islands = []
    const minDistance = 5 * dx
    const maxAttempts = 2000
    let attempts = 0
    const third = canvas.width / 3

    while (islands.length < 1 && attempts < maxAttempts) {
      const x = Math.random() * third
      const y = Math.random() * canvas.height
      islands.push({ x, y })
      attempts++
    }

    while (islands.length < 2 && attempts < maxAttempts) {
      const x = Math.random() * third + 2 * third
      const y = Math.random() * canvas.height
      if (islands.every(island => Math.hypot(x - island.x, y - island.y) >= minDistance)) {
        islands.push({ x, y })
      }
      attempts++
    }

    while (islands.length < 3 && attempts < maxAttempts) {
      const x = Math.random() * canvas.width
      const y = Math.random() * canvas.height
      if (islands.every(island => Math.hypot(x - island.x, y - island.y) >= minDistance)) {
        islands.push({ x, y })
      }
      attempts++
    }

    if (assignGreen) {
      while (islands.length < 4 && attempts < maxAttempts) {
        const x = Math.random() * canvas.width
        const y = Math.random() * canvas.height
        if (islands.every(island => Math.hypot(x - island.x, y - island.y) >= minDistance)) {
          islands.push({ x, y })
        }
        attempts++
      }
    }
  }

  function getElevation(x, y) {
    let max = 0
    for (const island of islands) {
      const dist = Math.hypot(x - island.x, y - island.y)
      if (dist < 200) {
        const level = Math.max(0, 1 - dist / 200)
        max = Math.max(max, level)
      }
    }
    return max
  }

  function hexPath(x, y, size) {
    const angle = Math.PI / 3
    ctx.beginPath()
    for (let i = 0; i < 6; i++) {
      ctx.lineTo(x + size * Math.cos(angle * i), y + size * Math.sin(angle * i))
    }
    ctx.closePath()
  }

  function drawHexWithPhase(x, y, size, elevation, phase, isGreen = false) {
    if (elevation > 0.01) {
      const shadowOffset = 5
      const blur = 10
      const alpha = 0.5 * elevation
      ctx.save()
      ctx.fillStyle = `${shadowColor}${Math.round(alpha * 255).toString(16).padStart(2, '0')}`
      ctx.translate(shadowOffset, shadowOffset)
      hexPath(x, y, size)
      ctx.filter = `blur(${blur}px)`
      ctx.fill()
      ctx.restore()
    }

    ctx.save()
    ctx.filter = 'none'
    ctx.fillStyle = isGreen ? '#17AB00' : backgroundColor
    hexPath(x, y, size)
    ctx.fill()
    ctx.restore()
  }

  function generateHexPositions() {
    hexPositions = []
    for (let row = 0; row * dy < canvas.height + dy; row++) {
      for (let col = 0; col * dx < canvas.width + dx; col++) {
        const offsetX = col * dx
        const offsetY = row * dy + (col % 2 === 1 ? dy / 2 : 0)
        hexPositions.push({ x: offsetX, y: offsetY })
      }
    }
  }

  function assignGreenHexagonsPerIsland() {
    greenHexIndices = []

    const greenIslands = islands.slice(0, 3)

    for (const island of greenIslands) {
      let minDist = Infinity
      let closestHexIndex = -1

      hexPositions.forEach((hexPos, idx) => {
        const dist = Math.hypot(island.x - hexPos.x, island.y - hexPos.y)
        if (dist < minDist) {
          minDist = dist
          closestHexIndex = idx
        }
      })

      if (!greenHexIndices.includes(closestHexIndex) && closestHexIndex !== -1) {
        greenHexIndices.push(closestHexIndex)
      }
    }
  }

  function drawHexPattern() {
    ctx.clearRect(0, 0, canvas.width, canvas.height)

    for (let i = 0; i < hexPositions.length; i++) {
      const { x, y } = hexPositions[i]
      const key = `${x},${y}`

      if (!phaseMap.has(key)) {
        phaseMap.set(key, Math.random() * 2 * Math.PI)
      }
      const phase = phaseMap.get(key)

      const baseElevation = elevationMap.get(key) ?? getElevation(x, y)
      elevationMap.set(key, baseElevation)

      const isGreen = greenHexIndices.includes(i)
      drawHexWithPhase(x, y, size, baseElevation, phase, isGreen)
    }
  }

  function resizeCanvas() {
    canvas.width = window.innerWidth
    canvas.height = window.innerHeight

    const scaleFactor = Math.min(canvas.width, canvas.height) / 700
    size = Math.max(30, Math.min(80, 55 * scaleFactor))

    dx = size * 3 / 2
    dy = Math.sqrt(3) * size

    elevationMap.clear()
    phaseMap.clear()

    generateHexPositions()
    if (assignGreen) assignGreenHexagonsPerIsland()
    drawHexPattern()
  }

  function init() {
    canvas.width = window.innerWidth
    canvas.height = window.innerHeight

    const scaleFactor = Math.min(canvas.width, canvas.height) / 700
    size = Math.max(30, Math.min(80, 55 * scaleFactor))

    dx = size * 3 / 2
    dy = Math.sqrt(3) * size

    generateIslands()
    generateHexPositions()
    if (assignGreen) assignGreenHexagonsPerIsland()
    drawHexPattern()
  }

  window.addEventListener('resize', resizeCanvas)
  init()
}

if (canvasHexagonsWhite) {
  initHexCanvas('hexWhite', '#F7F7FF', '#000000')
}
if (canvasHexagonsBlack) {
  initHexCanvas('hexBlack', '#111115', '#FFFFFF')
}

/**
 * TRANSITION PAGES
 */
const TRANSITION_DURATION = 1000
const DELAY_PER_ROW = 40

let isTransitioning = false

function playTransitionIn() {
  const backgrounds = document.querySelectorAll('.transition__background')
  if (!backgrounds.length) return

  const reversed = Array.from(backgrounds).reverse()
  reversed.forEach((bg, i) => {
    setTimeout(() => {
      bg.classList.remove('transition__background--hide')
      bg.classList.add('transition__background--reveal')
    }, i * DELAY_PER_ROW)
  })
}

function playTransitionOut(callback) {
  const backgrounds = document.querySelectorAll('.transition__background')
  if (!backgrounds.length) {
    callback()
    return
  }

  backgrounds.forEach((bg, i) => {
    setTimeout(() => {
      bg.classList.remove('transition__background--reveal')
      bg.classList.add('transition__background--hide')

      if (i === backgrounds.length - 1 && typeof callback === 'function') {
        setTimeout(() => {
          callback()
          isTransitioning = false
        }, TRANSITION_DURATION)
      }
    }, i * DELAY_PER_ROW)
  })
}

window.addEventListener('load', () => {
  playTransitionIn()
})

const links = document.querySelectorAll('.transition-link')
links.forEach(link => {
  link.addEventListener('click', e => {
    e.preventDefault()

    if (isTransitioning) return

    isTransitioning = true
    const target = link.getAttribute('href')

    playTransitionOut(() => {
      window.location.href = target
    })
  })
})

links.forEach(link => {
    link.addEventListener('click', function (e) {
      e.preventDefault()

      backgrounds.forEach((bg, i) => {
        setTimeout(() => {
          bg.classList.remove('transition__background--reveal')
          bg.classList.add('transition__background--hide')
        }, i * DELAY_PER_ROW)
      })

      const totalDelay = (backgrounds.length * DELAY_PER_ROW) + TRANSITION_DURATION
      setTimeout(() => {
        window.location.href = this.href
      }, totalDelay)
    })
})

/**
 * PAGES CODE
 */
//Home page
if (home) {
  /**
   * LOADING 
   */
  const bars = document.querySelectorAll('.progressBar__bar')
  const totalBars = bars.length

  const MIN_TIME = 5000
  const startTime = Date.now()

  const resources = document.images.length
  let loadedResources = 0
  let progress = 0

  const loadingElement = document.querySelector('.loading')

  const hasSeenLoader = sessionStorage.getItem('hasSeenLoader')

  if (!hasSeenLoader) {
    loadingElement.classList.add('loading--show')

    function updateProgressBar() {
      const barsToActivate = Math.floor(progress * totalBars)
      bars.forEach((bar, index) => {
        if (index < barsToActivate) {
          bar.classList.add('progressBar__bar--loaded')
        } else {
          bar.classList.remove('progressBar__bar--loaded')
        }
      })
    }

    function checkIfFinished() {
      const timeElapsed = Date.now() - startTime
      if (progress >= 1 && timeElapsed >= MIN_TIME) {
        loadingElement.classList.remove('loading--show')
        loadingElement.classList.add('loading--hide')
        sessionStorage.setItem('hasSeenLoader', 'true')
      }
    }

    function animate() {
      const timeElapsed = Date.now() - startTime
      const timeProgress = Math.min(timeElapsed / MIN_TIME, 1)
      const loadProgress = resources === 0 ? 1 : loadedResources / resources

      progress = Math.min(timeProgress, loadProgress)
      updateProgressBar()

      if (progress < 1 || timeElapsed < MIN_TIME) {
        requestAnimationFrame(animate)
      }

      checkIfFinished()
    }

    if (resources === 0) {
      loadedResources = 1
    } else {
      for (let i = 0; i < resources; i++) {
        const img = new Image()
        img.onload = img.onerror = () => {
          loadedResources++
        }
        img.src = document.images[i].src
      }
    }

    animate()
  } else {
    loadingElement.style.display = 'none'
  }

  /**
   * GSAP SECTIONS
   */
  let scaleValue
  const width = window.innerWidth

  if (width > 2000){
    scaleValue = 85
  } else if (width > 1500) {
    scaleValue = 75
  }  else {
    scaleValue = 50
  }

  if(width >= 760){
    gsap.timeline({
      scrollTrigger: {
        trigger: ".secondSection",
        start: "bottom bottom",           
        end: "+=100%",             
        scrub: 1,                
        pin: true,                  
      }
    })

    .to(".secondSection__anim", {
      scale: scaleValue,
      duration: 1,
      ease: "power3.out"
    })

    .to(".secondSection__anim", {
      scrollTrigger: {
        trigger: ".thirdSection",
        start: "top top",
        end: "+=100%",     
        scrub: 1
      },
      opacity: 0,
      duration: 1
    })

    ScrollTrigger.create({
      trigger: ".thirdSection",
      start: "top top",
      end: "+=100%",
      pin: true,
    })
  }

  /**
   * SMOOTH SCROLL
   */
  const smoother = ScrollSmoother.create({
    wrapper: '#smooth-wrapper',
    content: '#smooth-content',
    smooth: 1.5, 
    effects: true 
  })

  /**
   * HEXAGONS
   */
  initHexCanvas('hexCanvas', '#F7F7FF', '#000000', true)

  /**
   * Compteur
   */
  function getCountdownValues() {
    const now = new Date()
    const targetDate = new Date('2025-10-04T20:00:00')
    const diff = targetDate - now

    if (diff <= 0) return { days: 0, hours: 0, minutes: 0 }

    return {
      days: Math.floor(diff / (1000 * 60 * 60 * 24)),
      hours: Math.floor(diff / (1000 * 60 * 60)) % 24,
      minutes: Math.floor(diff / (1000 * 60)) % 60
    }
  }

  function getRandomRollingDigits(finalValue, progress) {
    const strFinal = String(finalValue).padStart(2, '0')
    let result = ''

    for (let i = 0; i < 2; i++) {
      const charFinal = strFinal[i]
      const threshold = 0.8 + i * 0.1

      if (progress < threshold) {
        const randomDigit = 1 + Math.floor(Math.random() * 9)
        result += randomDigit
      } else {
        result += charFinal
      }
    }
    return result
  }

  function animateRollingNumberRandom(element, finalValue, progress) {
    if (!element) return
    const textNode = [...element.childNodes].find(n => n.nodeType === 3)
    if (!textNode) return

    const displayStr = getRandomRollingDigits(finalValue, progress)
    textNode.textContent = displayStr
  }

  const { days, hours, minutes } = getCountdownValues()

  ScrollTrigger.create({
    trigger: ".fourthSection",
    start: "-10% center",
    end: "30% center",
    scrub: 1,
    onUpdate: self => {
      const daysEl = document.querySelector('#days.fourthSection__compteurEl')
      const hoursEl = document.querySelector('#hours.fourthSection__compteurEl')
      const minutesEl = document.querySelector('#minutes.fourthSection__compteurEl')

      if (self.progress === 1) {
        daysEl.textContent = String(days).padStart(2, '0')
        daysEl.insertAdjacentHTML('beforeend', '<span class="fourthSection__compteurEl--temps">jours</span>')

        hoursEl.textContent = String(hours).padStart(2, '0')
        hoursEl.insertAdjacentHTML('beforeend', '<span class="fourthSection__compteurEl--temps">heures</span>')

        minutesEl.textContent = String(minutes).padStart(2, '0')
        minutesEl.insertAdjacentHTML('beforeend', '<span class="fourthSection__compteurEl--temps">minutes</span>')
      } else {
        animateRollingNumberRandom(daysEl, days, self.progress)
        animateRollingNumberRandom(hoursEl, hours, self.progress)
        animateRollingNumberRandom(minutesEl, minutes, self.progress)
      }
    }
  })


  /**
   * THREE JS
   */
  //Settings
  const canvasSphere = document.querySelector('.abstractSphere')
  const sceneSphere = new THREE.Scene()

  const cameraSphere = new THREE.PerspectiveCamera(
    35,
    window.innerWidth / window.innerHeight,
    0.1,
    1000
  )
  cameraSphere.position.z = 5

  //Sound
  const soundButton = document.querySelector('.firstSection__sound')
  const bar = document.querySelectorAll('.firstSection__bar')
  const listener = new THREE.AudioListener()
  cameraSphere.add(listener)

  const sound = new THREE.Audio(listener)
  const audioLoader = new THREE.AudioLoader()
  audioLoader.load(
    '../assets/sound/ambientSound.mp3',
    function (buffer) {
      sound.setBuffer(buffer)
      sound.setLoop(true)
      sound.setVolume(1)
    },
    undefined,
    function (err) {
      console.error('Erreur de chargement audio :', err)
    }
  )

  soundButton.addEventListener('click', () => {
    const ctx = sound.context
    if (ctx && ctx.state === 'suspended') {
      ctx.resume()
    }

    if (!sound.isPlaying) {
      sound.play()
      for (let i = 0; i < bar.length; i++){
        bar[i].classList.add('firstSection__bar--play')
      }
    } else {
      sound.pause()
      for (let i = 0; i < bar.length; i++){
        bar[i].classList.remove('firstSection__bar--play')
      }
    }
  })

  //Sphere
  const renderer = new THREE.WebGLRenderer({ canvas: canvasSphere, alpha: true, antialias: true })
  renderer.setSize(window.innerWidth, window.innerHeight)
  renderer.setPixelRatio(window.devicePixelRatio)

  const lightSphere = new THREE.DirectionalLight(0xffffff, 5)
  lightSphere.position.set(10, 10, 20)
  sceneSphere.add(lightSphere)

  let modelSphere

  const loader = new GLTFLoader()
  loader.load(
    '../assets/3D/abstractSphere.glb',
    function (gltf) {
      modelSphere = gltf.scene
      updateSphereSettings() 
      sceneSphere.add(modelSphere)

      const spheres = []
      modelSphere.traverse((child) => {
        if (child.isMesh) {
          spheres.push(child)
        }
      })

      function animateSphere() {
        requestAnimationFrame(animateSphere)
        spheres.forEach((sphere, index) => {
          const directionX = index % 2 === 0 ? 1 : -1
          const directionY = index === 1 ? -1 : 1
          sphere.rotation.x += 0.001 * directionX
          sphere.rotation.y += 0.001 * directionY
        })
        renderer.render(sceneSphere, cameraSphere)
      }

      animateSphere()
    },
    undefined,
    function (error) {
      console.error('Erreur lors du chargement du modèle :', error)
    }
  )

  function updateSphereSettings() {
    if (!modelSphere) return

    let scaleValue
    let posMultiplier

    if (window.innerWidth <= 760) {
      scaleValue = 0.045
      posMultiplier = 0.3
    } else if (window.innerWidth <= 1200) {
      scaleValue = 0.055
      posMultiplier = 0.1
    } else if (window.innerWidth <= 1750){
      scaleValue = 0.075
      posMultiplier = -0.1
    } else if (window.innerWidth >= 2400){
      scaleValue = 0.075
      posMultiplier = 0
    } else if (window.innerWidth >= 2000){
      scaleValue = 0.075
      posMultiplier = -0.1
    } 
    else {
      scaleValue = 0.075
      posMultiplier = -0.1
    }

    modelSphere.scale.set(scaleValue, scaleValue, scaleValue)
    modelSphere.position.y = window.innerHeight * 0.0012 * posMultiplier
  }

  window.addEventListener('resize', () => {
    cameraSphere.aspect = window.innerWidth / window.innerHeight
    cameraSphere.updateProjectionMatrix()
    renderer.setSize(window.innerWidth, window.innerHeight)

    updateSphereSettings()
  })
}

//Dresscode page
else if (inspi) {
  const container = document.querySelector('.inspi')
  const btnLeft = document.querySelector('.inspi__btn--left')
  const btnRight = document.querySelector('.inspi__btn:not(.inspi__btn--left)')
  const scrollStep = 350
  const bars = document.querySelectorAll('.progressBar__bar')
  const els = document.querySelectorAll('.inspi__el')
  const maxShift = 15

  let targetScrollLeft = container.scrollLeft
  let animationFrameId = null

  function updateButtons() {
    const tolerance = 2
    if (targetScrollLeft <= tolerance) {
      btnLeft.classList.add('inspi__btn--disabled')
    } else {
      btnLeft.classList.remove('inspi__btn--disabled')
    }
    if (targetScrollLeft + container.clientWidth >= container.scrollWidth - tolerance) {
      btnRight.classList.add('inspi__btn--disabled')
    } else {
      btnRight.classList.remove('inspi__btn--disabled')
    }
  }

  function updateProgressBars() {
    const maxScrollLeft = container.scrollWidth - container.clientWidth
    const progress = container.scrollLeft / maxScrollLeft
    const barsCount = bars.length
    const barsToActivate = Math.round(progress * barsCount)

    bars.forEach((bar, index) => {
      bar.classList.toggle('progressBar__bar--loaded', index < barsToActivate)
    })
  }

  function updateParallax() {
    els.forEach((el) => {
      const img = el.querySelector('.inspi__img')
      if (!img) return

      const elRect = el.getBoundingClientRect()
      const containerRect = container.getBoundingClientRect()
      const elCenterX = elRect.left + elRect.width / 2
      const relativeX = elCenterX - containerRect.left
      const normalizedDistance = (relativeX - container.clientWidth / 2) / (container.clientWidth / 2)
      const shift = -normalizedDistance * maxShift

      img.style.transform = `translateX(${shift}px)`
    })
  }

  let ticking = false
  container.addEventListener('scroll', () => {
    updateProgressBars()
    if (!ticking) {
      requestAnimationFrame(() => {
        updateParallax()
        updateButtons()
        ticking = false
      })
      ticking = true
    }
  })

  function smoothScrollToTarget() {
    const currentScroll = container.scrollLeft
    const distance = targetScrollLeft - currentScroll
    const step = distance * 0.05

    if (Math.abs(distance) > 0.5) {
      container.scrollLeft = currentScroll + step
      animationFrameId = requestAnimationFrame(smoothScrollToTarget)
    } else {
      container.scrollLeft = targetScrollLeft
      cancelAnimationFrame(animationFrameId)
      animationFrameId = null
      updateButtons()
      updateProgressBars()
      updateParallax()
    }
  }

  container.addEventListener('wheel', (e) => {
    e.preventDefault()
    targetScrollLeft += e.deltaY + e.deltaX
    targetScrollLeft = Math.max(0, Math.min(targetScrollLeft, container.scrollWidth - container.clientWidth))

    if (!animationFrameId) {
      smoothScrollToTarget()
    }
    
  }, { passive: false })

  let touchStartX = 0
  container.addEventListener('touchstart', (e) => {
    touchStartX = e.touches[0].clientX
  }, { passive: true })

  container.addEventListener('touchmove', (e) => {
    const touchX = e.touches[0].clientX
    const deltaX = touchStartX - touchX

    if (Math.abs(deltaX) > 2) {
      targetScrollLeft += deltaX
      targetScrollLeft = Math.max(0, Math.min(targetScrollLeft, container.scrollWidth - container.clientWidth))

      if (!animationFrameId) {
        smoothScrollToTarget()
      }
      touchStartX = touchX
      e.preventDefault()
    }
  }, { passive: false })

  btnLeft.addEventListener('click', () => {
    if (btnLeft.classList.contains('inspi__btn--disabled')) return
    targetScrollLeft = Math.max(0, targetScrollLeft - scrollStep)
    if (!animationFrameId) smoothScrollToTarget()
  })

  btnRight.addEventListener('click', () => {
    if (btnRight.classList.contains('inspi__btn--disabled')) return
    targetScrollLeft = Math.min(container.scrollWidth - container.clientWidth, targetScrollLeft + scrollStep)
    if (!animationFrameId) smoothScrollToTarget()
  })

  updateButtons()
  updateProgressBars()
  updateParallax()
}

//Response page
else if(form){
  //Form
  const urlParams = new URLSearchParams(window.location.search)
  const statut = urlParams.get('statut')

  if (statut === 'ok') {
    sessionStorage.setItem('formSubmitted', 'true')

    window.history.replaceState({}, document.title, window.location.pathname)
  }

  if (sessionStorage.getItem('formSubmitted') === 'true') {
    document.querySelector('.form')?.classList.add('form--hide')
    document.querySelector('.check')?.classList.add('check--show')
    document.querySelector('.planet')?.classList.add('planet--done')
  }

 
  //Planet
  const canvas = document.querySelector('.planet')
  const scene = new THREE.Scene()

  const renderer = new THREE.WebGLRenderer({ canvas, alpha: true, antialias: true })
  renderer.setPixelRatio(window.devicePixelRatio)

  let camera, sphere, light
  const textureLoader = new THREE.TextureLoader()

  function createScene() {
    if (sphere) {
      scene.remove(sphere)
      sphere.geometry.dispose()
      sphere.material.dispose()
    }
    if (light) {
      scene.remove(light)
    }

    const canvasWidth = canvas.clientWidth
    const canvasHeight = canvas.clientHeight
    renderer.setSize(canvasWidth, canvasHeight)

    const isWide = window.innerWidth >= 950

    const radius = isWide ? window.innerHeight : window.innerWidth

    camera = new THREE.OrthographicCamera(
      -canvasWidth / 2,
      canvasWidth / 2,
      canvasHeight / 2,
      -canvasHeight / 2,
      0.1,
      2000
    )

    const baseZ = 800
    const minHeight = 800
    const scale = window.innerHeight / minHeight
    camera.position.z = baseZ * Math.max(scale, 1)

    const texture = textureLoader.load('../assets/3D/texturePlanet.jpeg')

    const material = new THREE.MeshPhongMaterial({
      map: texture,
    })

    const geometry = new THREE.SphereGeometry(radius, 50, 50)
    sphere = new THREE.Mesh(geometry, material)

    if (isWide) {
      sphere.position.x = canvasWidth / 2 - radius
      sphere.position.y = 0
    } else {
      sphere.position.x = 0
      sphere.position.y = -radius + canvasHeight / 2
    }

    scene.add(sphere)

    light = new THREE.DirectionalLight(0xf7f7ff, isWide ? 4 : 1.5)
    light.position.set(0, isWide ? 0.01 : 1, 20)
    scene.add(light)
  }

  function animate() {
    requestAnimationFrame(animate)
    if (sphere) {
      sphere.rotation.y += 0.0005
      sphere.rotation.x -= 0.0004
    }
    renderer.render(scene, camera)
  }

  window.addEventListener('resize', createScene)

  createScene()
  animate()
}